import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const siteKey = process.env.REACT_APP_SITE_RECAPTCHA_KEY;

const baseFieldClassName = 'form-field';

function statusMessage(status) {
  if (status === 'success') {
    return 'Thanks. Your submission is with the Rivelin team.';
  }

  if (status === 'error') {
    return 'Something went wrong. Please try again or email info@rivelinrobotics.com.';
  }

  return '';
}

function createInitialState(fields, formName) {
  return fields.reduce(
    (state, field) => ({
      ...state,
      [field.name]: field.defaultValue || '',
    }),
    {
      'bot-field': '',
      'form-name': formName,
    }
  );
}

export function LeadForm({
  formName,
  title,
  intro,
  fields,
  submitLabel,
  includeFileUpload = false,
  includeRoleOptions = [],
}) {
  const [values, setValues] = useState(createInitialState(fields, formName));
  const [status, setStatus] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const fileField = useMemo(() => fields.find((field) => field.type === 'file'), [fields]);

  useEffect(() => {
    setValues(createInitialState(fields, formName));
  }, [fields, formName]);

  const handleChange = (event) => {
    const { name, value, files, type } = event.target;
    setValues((currentValues) => ({
      ...currentValues,
      [name]: type === 'file' ? files?.[0] || null : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('idle');

    try {
      let token = '';
      if (siteKey && recaptchaRef.current) {
        token = await recaptchaRef.current.executeAsync();
      }

      const formData = new FormData();
      formData.append('form-name', formName);
      formData.append('bot-field', values['bot-field'] || '');

      if (token) {
        formData.append('g-recaptcha-response', token);
      }

      Object.keys(values).forEach((key) => {
        if (['form-name', 'bot-field'].includes(key)) {
          return;
        }

        if (values[key] === null || values[key] === undefined || values[key] === '') {
          return;
        }

        formData.append(key, values[key]);
      });

      const response = await fetch('/__forms.html', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Unexpected response status ${response.status}`);
      }

      setStatus('success');
      setValues(createInitialState(fields, formName));

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      console.error(`Failed to submit form ${formName}.`, error);
      setStatus('error');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='lead-form-card'>
      <div className='lead-form-copy'>
        <p className='section-label'>{title}</p>
        <h2>{intro}</h2>
      </div>
      <form
        className='lead-form'
        name={formName}
        method='POST'
        data-netlify='true'
        data-netlify-recaptcha='true'
        netlify-honeypot='bot-field'
        onSubmit={handleSubmit}
      >
        <input type='hidden' name='form-name' value={formName} />
        <input
          type='text'
          name='bot-field'
          value={values['bot-field']}
          onChange={handleChange}
          className='honeypot-field'
          tabIndex='-1'
          autoComplete='off'
        />

        {fields.map((field) => {
          if (field.type === 'select') {
            return (
              <label key={field.name} className={baseFieldClassName}>
                <span>{field.label}</span>
                <select name={field.name} value={values[field.name]} onChange={handleChange} required={field.required}>
                  {includeRoleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          }

          if (field.type === 'textarea') {
            return (
              <label key={field.name} className={baseFieldClassName}>
                <span>{field.label}</span>
                <textarea
                  name={field.name}
                  placeholder={field.placeholder}
                  value={values[field.name]}
                  onChange={handleChange}
                  required={field.required}
                  rows={5}
                />
              </label>
            );
          }

          if (field.type === 'file') {
            return (
              <label key={field.name} className={baseFieldClassName}>
                <span>{field.label}</span>
                <input
                  type='file'
                  name={field.name}
                  accept={field.accept}
                  onChange={handleChange}
                  required={field.required}
                />
                <small>Netlify Forms supports a single attachment up to 8 MB.</small>
              </label>
            );
          }

          return (
            <label key={field.name} className={baseFieldClassName}>
              <span>{field.label}</span>
              <input
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                value={values[field.name]}
                onChange={handleChange}
                required={field.required}
              />
            </label>
          );
        })}

        {includeFileUpload && !fileField ? null : null}

        {siteKey ? (
          <ReCAPTCHA ref={recaptchaRef} size='invisible' sitekey={siteKey} onChange={() => undefined} />
        ) : null}

        <button className='primary-cta submit-button' type='submit' disabled={submitting}>
          {submitting ? 'Sending…' : submitLabel}
        </button>
        {status !== 'idle' ? <p className={`form-status ${status}`}>{statusMessage(status)}</p> : null}
      </form>
    </div>
  );
}
